/**
 * Управляет префиксом счётчика непросмотренных заказов в `document.title`.
 *
 * Сложность: хук `usePageMeta` на каждой странице жёстко перезаписывает
 * `document.title`. Чтобы префикс `(N)` переживал навигацию, вешаем
 * `MutationObserver` на элемент `<title>` и переустанавливаем префикс после
 * любой чужой перезаписи. Собственную запись отмечаем флагом, чтобы не
 * зациклить наблюдатель.
 */

let count = 0;
let observer: MutationObserver | null = null;
let isApplying = false;

/** Убирает наш префикс `(N) ` из заголовка, если он есть. */
const stripPrefix = (title: string): string =>
  title.replace(/^\(\d+\)\s/, '');

/** Собирает итоговый заголовок: префикс счётчика + базовый текст страницы. */
const buildTitle = (): string => {
  const base = stripPrefix(document.title);
  return count > 0 ? `(${count}) ${base}` : base;
};

/** Записывает заголовок, не вызывая реакцию собственного наблюдателя. */
const applyTitle = (): void => {
  const next = buildTitle();
  if (next === document.title) {
    return;
  }

  isApplying = true;
  document.title = next;
  isApplying = false;
};

const getTitleElement = (): HTMLTitleElement | null =>
  document.querySelector('title');

/** Запускает слежение за `<title>` (идемпотентно). */
const start = (): void => {
  if (observer !== null) {
    return;
  }

  const titleElement = getTitleElement();
  if (titleElement === null) {
    return;
  }

  observer = new MutationObserver(() => {
    if (isApplying) {
      return;
    }
    applyTitle();
  });

  observer.observe(titleElement, { childList: true });
};

/** Останавливает слежение и снимает префикс. */
const stop = (): void => {
  observer?.disconnect();
  observer = null;
  count = 0;
  applyTitle();
};

/** Устанавливает число непросмотренных и обновляет заголовок. */
const sync = (nextCount: number): void => {
  count = nextCount;
  start();
  applyTitle();
};

export const tabTitleNotifier = { sync, stop };
